import React, { useState, useEffect } from 'react';
import NavBar from '../NavBar';
import { auth } from '../firebaseConfig';
import { supabase } from '../supabaseClient';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState([]);
  const [showDesktopMessage, setShowDesktopMessage] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(true);

  // Auth listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(u => {
      setUser(u || null);
    });
    return () => unsubscribe();
  }, []);

  // Resize listener
  useEffect(() => {
    const handleResize = () => setShowDesktopMessage(window.innerWidth < 650);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch subscription + org membership + bugs
  useEffect(() => {
    if (!user?.uid) return;

    const fetchSubscription = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('membershipType')
          .eq('UID', user.uid)
          .maybeSingle();

        if (error) throw error;
        setSubscriptionTier(data?.membershipType ?? null);
      } catch (err) {
        console.error('Error fetching subscription: ', err);
      }
    };

    const fetchOrgsAndBugs = async () => {
      setLoading(true);
      try {
        // 1) Find orgIDs this user belongs to (THIS is the key fix)
        const { data: memberships, error: memberError } = await supabase
          .from('orgMembers')
          .select('orgID, memberID')
          .eq('memberID', user.uid);

        if (memberError) throw memberError;

        const orgIDs = (memberships || [])
          .map(m => m.orgID)
          .filter(v => v !== null && v !== undefined);

        if (orgIDs.length === 0) {
          setOrg([]);
          setBugs([]);
          setLoading(false);
          return;
        }

        // 2) (Optional) fetch org records for those orgIDs (if you need org info in UI)
        const { data: orgsData, error: orgError } = await supabase
          .from('orgs')
          .select('*')
          .in('orgID', orgIDs);

        if (orgError) throw orgError;
        setOrg(orgsData || []);

        // 3) Fetch all bugs for all orgs in one query (no loops)
        const { data: bugsData, error: bugError } = await supabase
          .from('bugs')
          .select('*')
          .in('orgID', orgIDs);

        if (bugError) throw bugError;
        setBugs(bugsData || []);
      } catch (err) {
        console.error('Error fetching orgs/bugs: ', err);
        setOrg([]);
        setBugs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
    fetchOrgsAndBugs();
  }, [user]);

  // ----- UI guards -----
  if (!user) {
    return (
      <div className='dashboard bg-dark-main h-screen'>
        <NavBar />
        <div className="mx-auto p-4 mt-12 container">
          <h1 className="text-3xl font-bold mb-4 text-white-default">Dashboard</h1>
          <p className="text-white-default">You must be logged in to see this page...</p>
        </div>
      </div>
    );
  }

  if (showDesktopMessage) {
    return (
      <div className='dashboard bg-dark-main h-screen'>
        <NavBar />
        <div className="mx-auto p-4 mt-12 container">
          <h1 className="text-white-default text-xl">
            This page is not available on mobile devices. Please use a desktop or laptop to view this page.
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div className='dashboard bg-dark-main h-screen transition ease-in-out duration-300'>
      {subscriptionTier === 'free' && isUpgradeOpen && (
        <div className="bg-purple w-screen text-center text-white-default p-4 rounded-md mb-4 flex items-center justify-center relative">
          <div className="flex-grow">
            You are on the free tier.{' '}
            <a href="/subscribe" className="underline hover:text-gray-dark">Upgrade now</a> for more features!
          </div>
          <div
            className="absolute right-4 cursor-pointer hover:text-gray-dark"
            onClick={() => setIsUpgradeOpen(false)}
          >
            &#10006;
          </div>
        </div>
      )}

      <NavBar />
      <div className="mx-auto p-4 mt-12 container">
        <h1 className="text-3xl font-bold mb-4 text-white-default">Dashboard</h1>

        {loading ? (
          <p className="text-white-default mt-8">Loading...</p>
        ) : (
          <>
            <div className="mt-16 container mx-auto p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"></div>

            <div className="flex flex-wrap -mx-4">
              <div className="w-full md:w-1/2 lg:w-1/3 p-4">
                <div className="shadow-lg rounded-md p-6 bg-card-default flex flex-col justify-center items-center">
                  <h2 className="text-xl font-semibold mb-2 text-white-default bg-gray-400 h-6 w-3/4 rounded-md text-center">
                    Bugs Created:
                  </h2>
                  <h2 className="text-xl font-semibold mb-2 text-white-default bg-gray-400 h-6 w-3/4 rounded-md text-center truncate">
                    {bugs.filter(bug => bug.bugAuthor === user.uid).length}
                  </h2>
                </div>
              </div>

              <div className="w-full md:w-1/2 lg:w-1/3 p-4">
                <div className="shadow-lg rounded-md p-6 bg-card-default flex flex-col justify-center items-center">
                  <h2 className="text-xl font-semibold mb-2 text-white-default bg-gray-400 h-6 w-3/4 rounded-md text-center">
                    Bugs Open:
                  </h2>
                  <h2 className="text-xl font-semibold mb-2 text-white-default bg-gray-400 h-6 w-3/4 rounded-md text-center truncate">
                    {bugs.filter(bug =>
                      (bug.isOpen && bug.bugAuthor === user.uid) ||
                      (bug.isOpen && Array.isArray(bug.assignedMembers) && bug.assignedMembers.includes(user.uid))
                    ).length}
                  </h2>
                </div>
              </div>

              <div className="w-full md:w-1/2 lg:w-1/3 p-4">
                <div className="shadow-lg rounded-md p-6 bg-card-default flex flex-col justify-center items-center">
                  <h2 className="text-xl font-semibold mb-2 text-white-default bg-gray-400 h-6 w-3/4 rounded-md text-center">
                    Assigned Bugs:
                  </h2>
                  <h2 className="text-xl font-semibold mb-2 text-white-default bg-gray-400 h-6 w-3/4 rounded-md text-center truncate">
                    {bugs.filter(bug =>
                      Array.isArray(bug.assignedMembers) && bug.assignedMembers.includes(user.uid)
                    ).length}
                  </h2>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
